/* TranscriptRequest — simple form + history list. Lets the student request
 * an academic transcript via email PDF or postal delivery. Status is shown
 * (pending / processing / sent / declined) so they don't have to email and ask. */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Mail, MapPin, Clock, Check, X, Send } from 'lucide-react';
import PortalTopBar from '../../components/academy/PortalTopBar';
import { useAuth } from '../../context/AuthContext';

const STATUS_PILL = {
  pending:    { Icon: Clock, cls: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Pending' },
  processing: { Icon: Send,  cls: 'bg-sky-50 text-sky-700 border-sky-200',         label: 'Processing' },
  sent:       { Icon: Check, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Sent' },
  declined:   { Icon: X,     cls: 'bg-red-50 text-red-700 border-red-200',         label: 'Declined' },
};

export default function TranscriptRequest() {
  const { api } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [delivery, setDelivery] = useState('email');
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientAddress, setRecipientAddress] = useState('');
  const [purpose, setPurpose] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/academy/my/transcript-requests');
      setRows(data.rows || []);
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { reload(); }, [reload]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(''); setSuccess('');
    try {
      await api.post('/api/academy/my/transcript-requests', {
        delivery_method: delivery,
        recipient_name: recipientName,
        recipient_email: delivery === 'email' ? recipientEmail : null,
        recipient_address: delivery === 'mail' ? recipientAddress : null,
        purpose: purpose || null,
      });
      setSuccess('Request submitted. We\'ll email you when it\'s on its way.');
      setRecipientName(''); setRecipientEmail(''); setRecipientAddress(''); setPurpose('');
      await reload();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit request');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <PortalTopBar />
      <main className="max-w-[1100px] mx-auto px-4 lg:px-6 py-8">
        <Link to="/academy/dashboard" className="inline-flex items-center gap-1.5 text-[13px] text-[#6E6E73] hover:text-[#c9a227] mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Dashboard
        </Link>
        <h1 className="text-[28px] font-extrabold text-[#1D1D1F] mb-1">Transcript Request</h1>
        <p className="text-[14px] text-[#6E6E73] mb-7">Request an official transcript by email or post.</p>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)] gap-6">
          {/* ----- Form ----- */}
          <form onSubmit={submit} data-testid="transcript-form"
            className="rounded-2xl border border-[#E5E5E7] bg-white p-6 h-fit">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-4 h-4 text-[#c9a227]" />
              <span className="text-[13px] font-bold tracking-[0.18em] uppercase text-[#0e0e10]">New request</span>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-3" role="radiogroup" aria-label="Delivery method">
              {[
                { id: 'email', label: 'Email PDF',     Icon: Mail },
                { id: 'mail',  label: 'Mailed (post)', Icon: MapPin },
              ].map(({ id, label, Icon }) => (
                <button key={id} type="button" onClick={() => setDelivery(id)}
                  data-testid={`transcript-delivery-${id}`}
                  className={`px-3 py-2 rounded-xl border text-[12.5px] font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5
                    ${delivery === id ? 'border-[#c9a227] bg-[#fff8e7] text-[#a8871a]' : 'border-[#E5E5E7] text-[#6E6E73] hover:border-[#c9a227]'}`}>
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              ))}
            </div>

            <label className="block mb-3">
              <span className="block text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#a8871a] mb-1">Recipient name</span>
              <input required value={recipientName} onChange={(e) => setRecipientName(e.target.value)}
                data-testid="transcript-recipient-name"
                placeholder="e.g. Acme HR Department"
                className="w-full rounded-lg border border-[#E5E5E7] px-3 py-2 text-[13px] outline-none focus:border-[#c9a227]" />
            </label>

            {delivery === 'email' ? (
              <label className="block mb-3">
                <span className="block text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#a8871a] mb-1">Recipient email</span>
                <input required type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)}
                  data-testid="transcript-recipient-email"
                  placeholder="hr@example.com"
                  className="w-full rounded-lg border border-[#E5E5E7] px-3 py-2 text-[13px] outline-none focus:border-[#c9a227]" />
              </label>
            ) : (
              <label className="block mb-3">
                <span className="block text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#a8871a] mb-1">Mailing address</span>
                <textarea required rows={3} value={recipientAddress} onChange={(e) => setRecipientAddress(e.target.value)}
                  data-testid="transcript-recipient-address"
                  placeholder="Street, city, state, ZIP, country"
                  className="w-full rounded-lg border border-[#E5E5E7] px-3 py-2 text-[13px] outline-none focus:border-[#c9a227]" />
              </label>
            )}

            <label className="block mb-4">
              <span className="block text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#a8871a] mb-1">Purpose <span className="text-[#9aa0a6] font-normal lowercase">(optional)</span></span>
              <input value={purpose} onChange={(e) => setPurpose(e.target.value)}
                data-testid="transcript-purpose"
                placeholder="e.g. Job application, university transfer"
                className="w-full rounded-lg border border-[#E5E5E7] px-3 py-2 text-[13px] outline-none focus:border-[#c9a227]" />
            </label>

            {success && <div data-testid="transcript-success" className="mb-3 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-[12.5px] text-emerald-800">{success}</div>}
            {error && <div data-testid="transcript-error" className="mb-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-[12.5px] text-red-700">{error}</div>}

            <button type="submit" disabled={busy} data-testid="transcript-submit"
              className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#0e0e10] disabled:opacity-50"
              style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)' }}>
              <Send className="w-3.5 h-3.5" /> {busy ? 'Submitting…' : 'Submit request'}
            </button>
          </form>

          {/* ----- History ----- */}
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#a8871a] mb-2">Your previous requests</div>
            {loading ? (
              <div className="text-[#6E6E73] text-[13px]">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-[#E5E5E7] bg-white p-8 text-center">
                <FileText className="w-9 h-9 text-[#c9a227] mx-auto mb-2" />
                <div className="text-[14px] font-bold text-[#1D1D1F]">No transcript requests yet</div>
                <p className="text-[12.5px] text-[#6E6E73]">Submit one using the form on the left.</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {rows.map((r) => {
                  const meta = STATUS_PILL[r.status] || STATUS_PILL.pending;
                  const Icon = meta.Icon;
                  return (
                    <li key={r.id} data-testid={`transcript-row-${r.id}`}
                      className="rounded-2xl border border-[#E5E5E7] bg-white p-4 flex items-start gap-3">
                      <span className="inline-flex h-9 w-9 shrink-0 rounded-xl bg-[#fff8e7] text-[#a8871a] items-center justify-center">
                        <FileText className="w-4 h-4" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="text-[13.5px] font-bold text-[#0e0e10]">
                            {r.recipient_name}
                          </div>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-wider border ${meta.cls}`}>
                            <Icon className="w-3 h-3" /> {meta.label}
                          </span>
                        </div>
                        <div className="text-[12px] text-[#6E6E73] mt-0.5">
                          {r.delivery_method === 'email'
                            ? <>Email · <span className="font-mono">{r.recipient_email}</span></>
                            : <>Post · {r.recipient_address}</>}
                        </div>
                        <div className="text-[11px] text-[#9aa0a6] mt-0.5">
                          Submitted {new Date(r.created_at).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                          {r.purpose && <> · {r.purpose}</>}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
